import React from "react";
import Link from "next/link";
import AdaptiveCriticalImage from "@components/atoms/AdaptiveCriticalImage";
import SingleCriticalImage from "@components/atoms/SingleCriticalImage";
import TpWidgetMicro from "../common/TpWidgetMicro";
import { useVideoAutoPlay } from "@components/atoms/common/useVideoAutoPlay";
type HeroProps = {
  id: string;
  data: {
    title: string;
    desc: string[];
    cta: {
      href: string;
      text: string;
    };
    img: {
      src: string;
      alt: string;
      prefixes?: {
        mobile: string;
        dpi: string;
      };
      dimensions?: [number, number];
      bg?: {
        src: string;
        alt: string;
        prefixes: {
          mobile: string;
          dpi: string;
        };
        dimensions?: [number, number];
      };
      fg?: {
        src: string;
        alt: string;
        prefixes: {
          mobile: string;
          dpi: string;
        };
        dimensions?: [number, number];
      };
    };
    video?: {
      thumbnail: string;
      preview: string;
    };
    hasTpWidget?: boolean;
  };
};

const Hero = ({ id, data }: HeroProps) => {
  const appMode = process.env.APP_MODE;
  const signupLink = data?.cta?.href?.[appMode] || data?.cta?.href;
  const Appointments = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left">
          <h1 className="text-xl lg:pt-9">{data.title}</h1>
          <p className="lg:maxw-md lg:mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary lg:px-7 md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
        </article>{" "}
        <figure className="lg:maxw-lg md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="lg:-ml-12 lg:mt-4 w-auto md:-ml-2 img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const TextReminder = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left lg:maxw-lg">
          <h1 className="text-xl lg:pt-5 lg:mw-lg">{data.title}</h1>
          <p className="lg:maxw-md mt-4 mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
        </article>{" "}
        <figure className="lg:maxw-lg md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="imgholder-quaternary img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const EmailReminder = () => {
    return (
      <section className="container grid--default has-align-top hero--default lg:mb-10 md:mb-10">
        <article className="text-left lg:maxw-lg">
          <h1 className="text-xl lg:pt-5 lg:mw-lg">{data.title}</h1>
          <p className="lg:maxw-md mt-4 lg:mb-2 md:mb-2 md:mt-1">
            {data.desc[0]}
          </p>
          <p className="lg:maxw-md lg:mb-5 md:mb-2">{data.desc[1]}</p>
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
        </article>{" "}
        <figure className="layered-images-wrap pos-rel lg:maxw-lg md:mt-5">
          <picture>
            <SingleCriticalImage
              src={data.img.bg?.src}
              alt={data.img.bg?.alt}
              className="imgholder-secondary bg-layer bg-custom-width md:pl-2 md:pr-2 lg:ml-5 img-fit"
              style={{ marginTop: 0 }}
              dimensions={data.img.bg?.dimensions}
            />
          </picture>
          <picture>
            <AdaptiveCriticalImage
              src={data.img.fg?.src}
              alt={data.img.fg?.alt}
              mobile={data.img.fg?.prefixes.mobile}
              dpi={data.img.fg?.prefixes.dpi}
              className="fg-layer bg-custom-width md:pr-2 img-fit imgholder-secondary"
              dimensions={data.img.fg?.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const StaffScheduling = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left lg:maxw-lg">
          <h1 className="text-xl lg:pt-5 lg:mw-lg">{data.title}</h1>
          <p className="lg:maxw-md mt-4 mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
        </article>{" "}
        <figure className="md:mt-5 pos-rel">
          <picture className="md:ml-2.5 md:mr-2.5">
            <AdaptiveCriticalImage
              src={data.img.bg?.src}
              alt={data.img.bg?.alt}
              mobile={data.img.bg?.prefixes?.mobile}
              dpi={data.img.bg?.prefixes.dpi}
              className="bg-layer img-fit imgholder-secondary"
              dimensions={data.img.bg?.dimensions}
            />
          </picture>
          <picture className="lg:-ml-8">
            <AdaptiveCriticalImage
              src={data.img.fg?.src}
              alt={data.img.fg?.alt}
              mobile={data.img.bg?.prefixes.mobile}
              dpi={data.img.bg?.prefixes.dpi}
              className="fg-layer imgholder-senary"
              dimensions={data.img.fg?.dimensions}
            />
            <style global jsx>
              {`
                .hero--default .fg-layer {
                  height: 100%;
                  bottom: 0px;
                }
              `}
            </style>
          </picture>
        </figure>
      </section>
    );
  };
  const Reviews = () => {
    return (
      <section className="container grid--default has-align-top hero--default pb-10 md:pb-5">
        <article className="text-left">
          <h1 className="text-xl lg:pt-5 lg:mw-lg">{data.title}</h1>
          <p className="lg:maxw-md mt-4 mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
        </article>{" "}
        <picture className="md:mt-5 lg:-ml-8">
          <AdaptiveCriticalImage
            src={data.img.src}
            alt={data.img.src}
            mobile={data.img.prefixes?.mobile}
            dpi={data.img.prefixes?.dpi}
            className="img-fit imgholder-secondary"
            dimensions={data.img.dimensions}
          />
        </picture>
      </section>
    );
  };
  const IosApp = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left lg:maxw-lg">
          <h1 className="text-xl lg:pt-9">{data.title}</h1>
          <p className="lg:maxw-md lg:mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <a
            href="https://apps.apple.com/us/app/setmore-appointments-appointment-scheduling-app/id665859578"
            target="_blank"
            rel="noopener noreferrer"
          >
            <SingleCriticalImage
              src="https://assets.setmore.com/website/v2/images/features/features-ios-app/app-store-badge@2x.png"
              alt="Download ios application"
              className="hero-logo"
              width="157"
              height="48"
            />
          </a>
        </article>{" "}
        <figure className="lg:maxw-lg md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.src}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="img-fit imgholder-basic"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const AndroidApp = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left">
          <h1 className="text-xl lg:pt-9">{data.title}</h1>
          <p className="lg:maxw-md lg:mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <a
            href="https://play.google.com/store/apps/details?id=com.adaptavant.setmore&amp;hl=en"
            target="_blank"
            rel="noopener noreferrer"
          >
            <SingleCriticalImage
              src="https://assets.setmore.com/website/v2/images/features/features-android-app/google-play-badge@2x.png"
              alt="Download android application"
              className="hero-logo"
              width="157"
              height="48"
            />
          </a>
        </article>{" "}
        <figure className="lg:maxw-mds md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.src}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="lg:-ml-8 lg:w-auto md:w-100 imgholder-basic img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const DesktopApp = () => {
    return (
      <section className="container grid--default has-align-top hero--default lg:pb-10 md:pb-5">
        <article className="text-left">
          <h1 className="text-xl lg:pt-5">{data.title}</h1>
          <p className="lg:maxw-md lg:mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <div className="device-btn-wrap grid--default fs-0 lg:maxw-mds">
            <a
              href={data.cta[0].href}
              className="w-auto btn-secondary lg:pl-2.5 lg:pr-2.5 md:pl-2.5 md:pr-2.5"
            >
              <i dangerouslySetInnerHTML={{ __html: data.cta[0].icon }}></i>
              <span>{data.cta[0].text}</span>
            </a>{" "}
            <a
              href={data.cta[1].href}
              className="w-auto btn-secondary md:mt-2.5 lg:pl-2.5 lg:pr-2.5 md:pl-2.5 md:pr-5"
            >
              <i dangerouslySetInnerHTML={{ __html: data.cta[1].icon }}></i>
              <span>{data.cta[1].text}</span>
            </a>
          </div>
        </article>{" "}
        <figure className="md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="lg:-ml-10 md:-ml-2 w-auto img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const Payments = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left lg:maxw-lg">
          <h1 className="text-xl lg:pt-5">{data.title}</h1>
          <p className="lg:maxw-smd lg:mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <Link href={signupLink} prefetch={false} className="btn-primary">
            <span>{data.cta.text}</span>
          </Link>
        </article>{" "}
        <figure className="lg:maxw-lg md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              dimensions={data.img.dimensions}
              className="imgholder-basic img-fit"
            />
          </picture>
        </figure>
      </section>
    );
  };
  const GoogleSync = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left lg:maxw-lg">
          <h1 className="text-xl lg:pt-2.5">{data.title}</h1>
          <p
            className="lg:maxw-smd lg:mb-5 md:mt-2.5 md:mb-2.5"
            dangerouslySetInnerHTML={{ __html: data.desc[0] }}
          ></p>
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
        </article>{" "}
        <figure className="lg:maxw-lg md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="imgholder-tertiary img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const BookingPage = () => {
    return (
      <section className="lg:pb-10 md:pb-5 hero--default container">
        <div className="lg:d-fx mx-auto fx--sb">
          <article className="text-left lg:maxw-lg">
            <h1 className="text-xl lg:pt-5 lg:mw-lg">{data.title}</h1>
            <p className="lg:maxw-md mt-4 mb-5 md:mt-2.5 md:mb-2.5">
              {data.desc[0]}
            </p>
            <Link
              href={signupLink}
              prefetch={false}
              className="btn-primary md:w-100"
            >
              <span>{data.cta.text}</span>
            </Link>
            {data.hasTpWidget ? (
              <TpWidgetMicro className={`lg:mt-5 md:mt-3 md:pt-2}`} />
            ) : (
              ""
            )}
          </article>
          <figure className="lg:maxw-lg md:mt-5">
            <picture>
              <AdaptiveCriticalImage
                src={data.img.src}
                alt={data.img.alt}
                mobile={data.img.prefixes?.mobile}
                dpi={data.img.prefixes?.dpi}
                className="img-fit"
                dimensions={data.img.dimensions}
              />
            </picture>
          </figure>
        </div>
      </section>
    );
  };
  const ClassBooking = () => {
    return (
      <section className="container grid--default has-align-top hero--default md:pb-2">
        <article className="text-left lg:maxw-md">
          <h1 className="text-xl lg:pt-5">{data.title}</h1>
          <p className="lg:maxw-md lg:mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
        </article>{" "}
        <figure className="lg:maxw-lg md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="img-fit imgholder-tertiary"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const WebsiteWidget = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left lg:maxw-lg">
          <h1 className="text-xl lg:pt-5">{data.title}</h1>
          <p className="lg:maxw-smd lg:mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
        </article>{" "}
        <figure className="lg:maxw-lg md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="imgholder-basic img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const RecurringAppointments = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left">
          <h1 className="text-xl lg:pt-9">{data.title}</h1>
          <p
            className="lg:maxw-md lg:mb-5 md:mt-2.5 md:mb-2.5"
            dangerouslySetInnerHTML={{ __html: data.desc[0] }}
          />
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary lg:px-9 md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
        </article>{" "}
        <figure className="lg:maxw-lg md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="imgholder-secondary lg:mt-4 w-auto img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };

  const QRCode = () => {
    return (
      <section className="container grid--default has-align-top hero--default lg:mb-11 md:ml-0 md:mr-0">
        <article className="text-left lg:maxw-lg md:ml-2.5 md:mr-2.5">
          <h1 className="text-xl lg:pt-5">{data.title}</h1>
          <p
            className="lg:maxw-md mt-4 mb-5 md:mt-2.5 md:mb-2.5"
            dangerouslySetInnerHTML={{ __html: data.desc[0] }}
          />
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary lg:pl-12.5 lg:pr-12.5 md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
        </article>
        <figure>
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="imgholder-primary lg:ml-3 lg:mt-2 md:mb-5 md:mt-5 md:w-100 img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };

  const VirtualMeeting = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left lg:maxw-lg">
          <h1 className="text-xl lg:pt-5">{data.title}</h1>
          <p className="lg:maxw-smd lg:mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <Link href={signupLink} prefetch={false} className="btn-primary">
            <span>{data.cta.text}</span>
          </Link>
          <TpWidgetMicro className="trustpilot-widget-micro lg:mt-5 md:mt-4" />
        </article>{" "}
        <figure className="lg:maxw-lg md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              dimensions={data.img.dimensions}
              className="imgholder-basic img-fit"
            />
          </picture>
        </figure>
      </section>
    );
  };

  const YourBrandApp = () => {
    return (
      <section className="container grid--default has-align-top hero--default">
        <article className="text-left lg:maxw-lg">
          <h1 className="lg:pt-4">{data.title}</h1>
          <p className="lg:maxw-smd lg:mb-5 md:mt-2.5 md:mb-2.5">
            {data.desc[0]}
          </p>
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary md:w-100"
          >
            <span>{data.cta.text}</span>
          </Link>
          {data.hasTpWidget && (
            <TpWidgetMicro className={`lg:mt-5 md:mt-3 md:pt-2}`} />
          )}
        </article>{" "}
        <figure className="lg:maxw-lg md:mt-5">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="imgholder-primary img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };

  const LiveReceptionist = () => {
    return (
      <section className="container grid--default has-align-top hero--default pos-rel">
        <article className="text-left lg:maxw-lg">
          <h1 className="text-lg md:mb-2 lg:mb-3">{data.title}</h1>
          <p className="">{data.desc[0]}</p>
          <p
            className="lg:maxw-md lg:mt-1 lg:mb-5 md:mt-2 md:mb-3"
            dangerouslySetInnerHTML={{ __html: data.desc[1] }}
          ></p>
          <Link
            href={signupLink}
            prefetch={false}
            className="btn-primary"
            target="_blank"
          >
            <span>{data.cta.text}</span>
          </Link>
          <TpWidgetMicro className="trustpilot-widget-micro lg:mt-5 md:mt-4" />
        </article>
        <figure className="layered-images-wrap pos-rel lg:maxw-lg md:d-none">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes?.mobile}
              dpi={data.img.prefixes?.dpi}
              className="imgholder-primary img-fit"
              dimensions={data.img.dimensions}
            />
          </picture>
        </figure>
      </section>
    );
  };
  const HeroRender = () => {
    if (id === "features-appointments") {
      return <Appointments />;
    }
    if (id === "features-text-reminders") {
      return <TextReminder />;
    }
    if (id === "features-email-reminders") {
      return <EmailReminder />;
    }
    if (id === "features-staff-scheduling") {
      return <StaffScheduling />;
    }
    if (id === "features-reviews") {
      return <Reviews />;
    }
    if (id === "features-ios-app") {
      return <IosApp />;
    }
    if (id === "features-android-app") {
      return <AndroidApp />;
    }
    if (id === "features-desktop-app") {
      return <DesktopApp />;
    }
    if (id === "features-payments") {
      return <Payments />;
    }
    if (id === "features-google-calendar-sync") {
      return <GoogleSync />;
    }
    if (id === "features-booking-page") {
      return <BookingPage />;
    }
    if (id === "features-class-booking") {
      return <ClassBooking />;
    }
    if (id === "features-website-widgets") {
      return <WebsiteWidget />;
    }
    if (id === "features-recurring-appointments") {
      return <RecurringAppointments />;
    }
    if (id === "features-qr-code") {
      return <QRCode />;
    }
    if (id === "features-virtual-meetings") {
      return <VirtualMeeting />;
    }
    if (id === "features-your-branded-app") {
      return <YourBrandApp />;
    }
    if (id === "features-live-receptionist") {
      return <LiveReceptionist />;
    }
  };

  return <HeroRender />;
};

export default Hero;
